import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Shield, ArrowLeft, AlertCircle, Home } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL && process.env.REACT_APP_BACKEND_URL !== "undefined") ? process.env.REACT_APP_BACKEND_URL : "";
const API = `${BACKEND_URL}/api`;

export const AdminLoginPage = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login, isAuthenticated, loading: authLoading } = useAuth();

  // Auto-redirect to dashboard if already logged in via active session cookie
  React.useEffect(() => {
    if (isAuthenticated && !authLoading) {
      navigate('/eastend_admin/dashboard');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await axios.post(`${API}/ea_portal/auth`, {
        username,
        password
      }, { withCredentials: true });

      login(response.data.username);
      navigate('/eastend_admin/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Invalid credentials. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0a0a] flex items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Desktop Back Button */}
        <Link
          to="/"
          data-testid="back-to-home-link"
          className="hidden md:inline-flex items-center gap-2 text-[#FF9900] hover:text-white transition-colors mb-8"
        >
          <ArrowLeft className="w-5 h-5" />
          <span className="font-medium uppercase">Back to Home</span>
        </Link>

        {/* Mobile Back Button: Home Icon in Top Right Corner */}
        <Link
          to="/"
          data-testid="back-to-home-mobile"
          className="md:hidden fixed top-3 right-3 z-50 bg-[#1E1E1E] border border-[#27272A] p-3 text-[#FF9900] hover:bg-[#FF9900] hover:text-black transition-all duration-300 rounded-sm shadow-md flex items-center justify-center"
          title="Back to Home"
        >
          <Home className="w-6 h-6" />
        </Link>

        <div className="bg-[#121212] border border-[#27272A] p-8">
          <div className="flex items-center justify-center mb-8">
            <div className="w-16 h-16 bg-[#FF9900] flex items-center justify-center">
              <Shield className="w-10 h-10 text-black" />
            </div>
          </div>

          <h1 className="text-3xl font-bold text-[#FF9900] text-center mb-2 uppercase">Admin Access</h1>
          <p className="text-gray-400 text-center mb-8">Eastend Iron Industries Ltd.</p>

          {error && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-900/20 border border-red-900 text-red-400 p-4 mb-6 flex items-start gap-3"
              data-testid="login-error-message"
            >
              <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
              <p className="text-sm">{error}</p>
            </motion.div>
          )}

          <form onSubmit={handleSubmit} data-testid="admin-login-form" className="space-y-6">
            <div>
              <label htmlFor="username" className="block text-sm font-medium text-gray-400 mb-2">
                Username
              </label>
              <input
                id="username"
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                required
                data-testid="username-input"
                className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors"
                placeholder="Enter your username"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-400 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                data-testid="password-input"
                className="w-full bg-[#1E1E1E] border border-[#27272A] text-[#F3F4F6] px-4 py-3 focus:border-[#FF9900] focus:outline-none transition-colors"
                placeholder="Enter your password"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-button"
              className="w-full bg-[#FF9900] text-black px-8 py-4 font-bold uppercase tracking-wider hover:bg-white transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              For security purposes, only authorized personnel can access this area.
            </p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default AdminLoginPage;